// ============================================================
// store.js — single source of truth for app state
// ============================================================
// One bootstrap call on mount loads everything we need.
// Mutations call the API then update local state in place,
// avoiding extra round trips.
// ============================================================

import { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from './api.js';

export function useAppData() {
  const [state, setState] = useState({
    loading: true,
    error: null,
    categories: [],
    accounts: [],
    recurring: [],
    debts: [],
    sinking_funds: [],
    goals: [],
    budgets: [],
    settings: [],
    transactions: [],   // most recent 50 from bootstrap; full list lazy-loaded
    allTransactions: null,    // null = not loaded yet; [] = loaded empty; otherwise full ledger
    allTransactionsLoading: false,
  });

  const reload = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await api.bootstrap();
      setState({
        loading: false,
        error: null,
        categories: data.categories ?? [],
        accounts: data.accounts ?? [],
        recurring: data.recurring ?? [],
        debts: data.debts ?? [],
        sinking_funds: data.sinking_funds ?? [],
        goals: data.goals ?? [],
        budgets: data.budgets ?? [],
        settings: data.settings ?? [],
        transactions: data.recent_transactions ?? [],
        allTransactions: null,           // invalidate; consumers will refetch
        allTransactionsLoading: false,
      });
    } catch (e) {
      setState(s => ({ ...s, loading: false, error: e.message }));
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Lazy-load the full transaction ledger. Cached in state.allTransactions.
  // Multiple concurrent callers share one in-flight request via the
  // allTransactionsLoading flag; once data lands, all consumers re-render
  // from the same cache. Called by Dashboard, Forecast, Budgets, Trends —
  // any screen that needs more than the bootstrap's most-recent-50.
  const loadFullHistory = useCallback(async () => {
    let alreadyLoading = false;
    setState(s => {
      if (s.allTransactions !== null || s.allTransactionsLoading) {
        alreadyLoading = true;
        return s;
      }
      return { ...s, allTransactionsLoading: true };
    });
    if (alreadyLoading) return;
    try {
      // limit=100000 is effectively unbounded; the worker caps at 100k now.
      const rows = await api.listTransactions({ limit: 100000 });
      setState(s => ({ ...s, allTransactions: rows, allTransactionsLoading: false }));
    } catch (e) {
      // Don't permanently break the UI on a network blip — clear the loading
      // flag so a subsequent navigation can retry.
      setState(s => ({ ...s, allTransactionsLoading: false }));
      throw e;
    }
  }, []);

  // Lookups for category/account names by id
  const lookups = useMemo(() => ({
    catName: (id) => state.categories.find(c => c.id === id)?.name ?? '',
    acctName: (id) => state.accounts.find(a => a.id === id)?.name ?? '',
    setting: (key) => state.settings.find(s => s.key === key)?.value ?? null,
  }), [state.categories, state.accounts, state.settings]);

  // Mutators that keep local state in sync without re-fetching everything.
  // Note: transaction mutations also invalidate the allTransactions cache so
  // screens that consume the full ledger (Dashboard, Forecast, Budgets,
  // Trends) refetch on next mount. We don't try to merge in-place because
  // some operations are multi-row (transfers create two transactions, but
  // mutate.addTransaction only sees the response for one of them).
  const mutate = useMemo(() => ({
    addTransaction: async (t) => {
      const created = await api.createTransaction(t);
      setState(s => ({ ...s, transactions: [created, ...s.transactions], allTransactions: null }));
      return created;
    },
    updateTransaction: async (id, t) => {
      const upd = await api.updateTransaction(id, t);
      setState(s => ({
        ...s,
        transactions: s.transactions.map(x => x.id === id ? upd : x),
        allTransactions: null,
      }));
      return upd;
    },
    deleteTransaction: async (id) => {
      await api.deleteTransaction(id);
      setState(s => ({ ...s, transactions: s.transactions.filter(x => x.id !== id), allTransactions: null }));
    },

    addRecurring: async (r) => {
      const created = await api.createRecurring(r);
      setState(s => ({ ...s, recurring: [...s.recurring, created] }));
      return created;
    },
    updateRecurring: async (id, r) => {
      const upd = await api.updateRecurring(id, r);
      setState(s => ({ ...s, recurring: s.recurring.map(x => x.id === id ? upd : x) }));
      return upd;
    },
    deleteRecurring: async (id) => {
      await api.deleteRecurring(id);
      setState(s => ({ ...s, recurring: s.recurring.map(x => x.id === id ? { ...x, active: 0 } : x) }));
    },

    // generic CRUD generator for the simple tables — returns helpers
    // for `categories`, `accounts`, `debts`, `sinking_funds`, `goals`, `budgets`.
  }), []);

  // Generic helpers for all simple tables
  const generic = useMemo(() => {
    const tables = {
      categories: ['createCategory', 'updateCategory', 'deleteCategory'],
      accounts:   ['createAccount', 'updateAccount', 'deleteAccount'],
      debts:      ['createDebt', 'updateDebt', 'deleteDebt'],
      sinking_funds: ['createSinkingFund', 'updateSinkingFund', 'deleteSinkingFund'],
      goals:      ['createGoal', 'updateGoal', 'deleteGoal'],
      budgets:    ['createBudget', 'updateBudget', 'deleteBudget'],
    };
    const out = {};
    for (const [field, [c, u, d]] of Object.entries(tables)) {
      out[field] = {
        create: async (row) => {
          const created = await api[c](row);
          setState(s => ({ ...s, [field]: [...s[field], created] }));
          return created;
        },
        update: async (id, row) => {
          const upd = await api[u](id, row);
          setState(s => ({ ...s, [field]: s[field].map(x => x.id === id ? upd : x) }));
          return upd;
        },
        remove: async (id) => {
          await api[d](id);
          // For tables with `archived`, mark archived locally; for others (budgets), remove.
          setState(s => {
            const item = s[field].find(x => x.id === id);
            if (item && 'archived' in item) {
              return { ...s, [field]: s[field].map(x => x.id === id ? { ...x, archived: 1 } : x) };
            }
            return { ...s, [field]: s[field].filter(x => x.id !== id) };
          });
        },
      };
    }
    return out;
  }, []);

  return { state, lookups, mutate, generic, reload, loadFullHistory };
}
